import React, { useState, useEffect } from 'react';
import axios from '../../../Axiosinstance';
import {
  Typography,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Divider,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import Translate from '@mui/icons-material/Translate';
import VpnKey from '@mui/icons-material/VpnKey';
import { format } from 'date-fns';
import ExistingCompanyForm from './ExistingCompanyForm';
import NewCompanyForm from './NewCompanyForm';
import SelfSponsoredForm from './SelfSponsoredForm';
import { useParams } from 'react-router-dom'; // Import useParams to get batchid
import { LibraryBooks, Place, Group, AccessAlarm, AttachMoney } from '@mui/icons-material'; // Import icons

const Companies = () => {
  const { batchid } = useParams(); // Get the batchid from the URL params
  const [registrationType, setRegistrationType] = useState('company-sponsored');
  const [clientType, setClientType] = useState('');
  const [courseData, setCourseData] = useState(null); // State to store course data
  const [loading, setLoading] = useState(true); // State for loading status
  const [error, setError] = useState(null); // State for error handling

  useEffect(() => {
    // Fetch course data from API using batchid
    const fetchCourseData = async () => {
      try {
        const response = await axios.get(`/wsq/batches/${batchid}`);
        setCourseData(response.data); // Set the course data from the response
      } catch (err) {
        setError(err.message); // Set error message if fetching fails
      } finally {
        setLoading(false); // Set loading to false after fetching
      }
    };

    fetchCourseData();
  }, [batchid]);

  const handleRegistrationTypeChange = (event) => {
    setRegistrationType(event.target.value);
    setClientType('');
  };

  const handleClientTypeChange = (event) => {
    setClientType(event.target.value);
  };

  const renderCompanySponsoredForm = () => (
    <>
      <Divider sx={{ marginY: 2 }} />
      <FormControl component="fieldset" margin="normal">
        <Typography variant="subtitle1" gutterBottom>
          <strong>CLIENT TYPE:</strong>
        </Typography>
        <RadioGroup
          row
          aria-label="client-type"
          name="client-type"
          value={clientType}
          onChange={handleClientTypeChange}
        >
          <FormControlLabel
            value="existing-client"
            control={<Radio />}
            label="Existing Client Company"
          />
          <FormControlLabel
            value="new-client"
            control={<Radio />}
            label="New Client Company"
          />
        </RadioGroup>
      </FormControl>

      {/* Render Existing Client Company Form */}
      {clientType === 'existing-client' && courseData && <ExistingCompanyForm fee={courseData.course.fee} />}

      {/* Render New Client Company Form */}
      {clientType === 'new-client' && courseData && <NewCompanyForm fee={courseData.course.fee} />}
    </>
  );

  const renderSelfSponsoredForm = () => (
    <>
      <Divider sx={{ marginY: 2 }} />
      <Typography variant="subtitle1" gutterBottom>
        <strong>Self Sponsored Details:</strong>
      </Typography>

      {/* Render Self Sponsored Form */}
      <SelfSponsoredForm dataFrom="homecontainerpage" fee={courseData.course.fee} />    </>
  );

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 4,
        borderRadius: 2,
        border: '1px solid #ccc',
        maxWidth: '800px',
        margin: '20px auto',
        backgroundColor: '#f9f9f9',
      }}
    >
      <Typography variant="h5" gutterBottom>
        <strong>Company Sponsored Registration</strong>
      </Typography>

      {/* Display loading or error message */}
      {loading && <Typography>Loading course data...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      {/* Display course data if available */}
      {courseData && (
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
              📚 Course Information
            </Typography>
            <Divider />
            <br />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <LibraryBooks color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Name:</Typography>
                  <Typography variant="body2">{courseData.course.name}</Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Fee:</Typography>
                  <Typography variant="body2">${courseData.course.fee}</Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <Translate color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Language:</Typography>
                  <Typography variant="body2">{courseData.language}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <Place color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Venue:</Typography>
                  <Typography variant="body2">
                    {courseData.venue.place}, Room {courseData.venue.roomno}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <Group color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Intake Size:</Typography>
                  <Typography variant="body2">{courseData.intakeSize}</Typography>
                </Box>

                <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                  <VpnKey color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mr: 1 }}>Batch ID:</Typography>
                  <Typography variant="body2">{courseData.batchId}</Typography>
                </Box>
              </Grid>
            </Grid>

            {courseData.days && courseData.days.length > 0 && (
              <Box mt={3}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 'bold', color: 'primary.main' }}
                >
                  Session Details
                </Typography>
                <Grid container spacing={2} direction="column">
                  {courseData.days.map((day, index) => (
                    <Grid item xs={12} key={index}>
                      <Box
                        sx={{
                          p: 1,
                          borderLeft: 1,
                          borderColor: 'primary.light',
                        }}
                      >
                        <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Day {index + 1}: {format(new Date(day.date), 'dd:MM:yyyy')}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

              </Box>
            )}



          </CardContent>
        </Card>

      )}

      <FormControl component="fieldset" margin="normal">
        <Typography variant="subtitle1" gutterBottom>
          <strong>REGISTRATION TYPE:</strong>
        </Typography>
        <RadioGroup
          row
          aria-label="registration-type"
          name="registration-type"
          value={registrationType}
          onChange={handleRegistrationTypeChange}
        >
          <FormControlLabel
            value="company-sponsored"
            control={<Radio />}
            label="Company Sponsored"
          />
          <FormControlLabel
            value="self-sponsored"
            control={<Radio />}
            label="Self Sponsored"
          />
        </RadioGroup>
      </FormControl>

      {/* Render form based on registration type */}
      {registrationType === 'company-sponsored' && renderCompanySponsoredForm()}
      {registrationType === 'self-sponsored' && renderSelfSponsoredForm()}

    </Paper>
  );
};

export default Companies;
